import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../../services/authService';

// Catálogo de piezas por modelo (catalogoPiezas/{marca}__{claveModelo}/piezas)
// — lo alimenta finalizarVisita.js con cada visita; aquí oficina lo revisa,
// corrige el nombre, marca/desmarca "verificada" a mano, o borra una entrada
// mal catalogada (item 4, Rafa 2026-11-06). "Usado en este equipo"
// (catalogoPiezasPorActivo) no tiene edición propia todavía, a propósito.
export default function CatalogoPiezasAdminView() {
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busyId, setBusyId]   = useState(null); // `${modeloDocId}:${idPieza}` en curso
  const [filtro, setFiltro]   = useState('');
  const [abierto, setAbierto] = useState(null); // modeloDocId expandido
  const [editando, setEditando] = useState(null); // `${modeloDocId}:${idPieza}` con input de nombre abierto
  const [nombreEdit, setNombreEdit] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/getCatalogoPiezasAdmin', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al cargar');
      setModelos(d.modelos || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const modelosFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return modelos;
    return modelos
      .map(m => ({ ...m, piezas: m.piezas.filter(p =>
        p.referencia?.toLowerCase().includes(q) || p.nombre?.toLowerCase().includes(q) || m.claveModelo?.toLowerCase().includes(q)
      ) }))
      .filter(m => m.piezas.length > 0 || m.claveModelo?.toLowerCase().includes(q));
  }, [modelos, filtro]);

  const llamar = async (modeloDocId, idPieza, payload) => {
    const key = `${modeloDocId}:${idPieza}`;
    setBusyId(key);
    try {
      const r = await fetch('/.netlify/functions/editarPiezaCatalogo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ modeloDocId, idPieza, ...payload }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al guardar');
      if (payload.accion === 'borrar') {
        setModelos(prev => prev.map(m => m.modeloDocId !== modeloDocId ? m : { ...m, piezas: m.piezas.filter(p => p.id !== idPieza) }));
      } else {
        setModelos(prev => prev.map(m => m.modeloDocId !== modeloDocId ? m : {
          ...m, piezas: m.piezas.map(p => p.id !== idPieza ? p : { ...p, ...payload }),
        }));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
      setEditando(null);
    }
  };

  const fecha = s => (s ? new Date(s).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Cargando catálogo...</div>;

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Catálogo de piezas por modelo</h1>
          <p className="text-sm text-gray-500">{modelos.length} modelos · {modelos.reduce((s, m) => s + m.piezas.length, 0)} piezas</p>
        </div>
        <button onClick={cargar} disabled={loading}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl flex-shrink-0">
          {loading ? '…' : 'Actualizar'}
        </button>
      </div>

      <input
        type="text"
        value={filtro}
        onChange={e => setFiltro(e.target.value)}
        placeholder="Buscar por referencia, nombre o modelo..."
        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
      />

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

      {!loading && modelosFiltrados.length === 0 && !error && (
        <p className="text-sm text-gray-400 text-center py-12">{filtro ? 'Sin resultados' : '🎉 Catálogo vacío todavía'}</p>
      )}

      <div className="space-y-2">
        {modelosFiltrados.map(m => (
          <div key={m.modeloDocId} className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <button
              onClick={() => setAbierto(a => a === m.modeloDocId ? null : m.modeloDocId)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div>
                <span className="text-xs font-bold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 mr-2">{m.marca}</span>
                <span className="text-sm font-mono font-semibold text-gray-800">{m.claveModelo}</span>
              </div>
              <span className="text-xs text-gray-400">{m.piezas.length} piezas</span>
            </button>

            {abierto === m.modeloDocId && (
              <div className="divide-y divide-gray-50 border-t">
                {m.piezas.map(p => {
                  const key = `${m.modeloDocId}:${p.id}`;
                  return (
                    <div key={p.id} className="px-4 py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold bg-sky-100 text-sky-700 rounded-full px-2 py-0.5">{p.usos || 0}×</span>
                          {p.verificada && <span className="text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">✓ verificada</span>}
                          <span className="text-sm font-mono font-semibold text-gray-800">{p.referencia}</span>
                        </div>
                        {editando === key ? (
                          <div className="mt-1 flex items-center gap-2">
                            <input
                              type="text" value={nombreEdit} onChange={e => setNombreEdit(e.target.value)} autoFocus
                              className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded-lg text-sm"
                            />
                            <button
                              onClick={() => llamar(m.modeloDocId, p.id, { accion: 'editar', nombre: nombreEdit })}
                              disabled={busyId === key}
                              className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg"
                            >Guardar</button>
                            <button onClick={() => setEditando(null)} className="px-2 py-1 text-gray-400 text-xs">Cancelar</button>
                          </div>
                        ) : (
                          <p
                            className="text-sm text-gray-600 mt-0.5 cursor-pointer hover:underline"
                            title="Editar nombre"
                            onClick={() => { setEditando(key); setNombreEdit(p.nombre || ''); }}
                          >
                            {p.nombre || '(sin nombre)'}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Última vez {fecha(p.ultimaVez)}</p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => llamar(m.modeloDocId, p.id, { accion: 'editar', verificada: !p.verificada })}
                          disabled={busyId === key}
                          title={p.verificada ? 'Quitar verificada' : 'Marcar como verificada'}
                          className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg disabled:opacity-50"
                        >
                          {p.verificada ? '✓' : 'Verificar'}
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`¿Borrar "${p.referencia}" de este modelo?`)) llamar(m.modeloDocId, p.id, { accion: 'borrar' }); }}
                          disabled={busyId === key}
                          title="Borrar del catálogo"
                          className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg disabled:opacity-50"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
